import { TemplateErrorComponent } from "@/components/error";
import { toError } from "@/lib/to-error";
import { EditorLoading } from "@papier/ui/components/ui/editor-loading";
import { LanguageSwitcher } from "@/components/template/language-switcher";
import { EditorReviewActions } from "@/components/template/editor-review-actions";
import { orpc } from "@/lib/orpc";
import { environmentSearch } from "@/components/project/environment-search";
import type { LocaleEnum } from "@papier/projects-schema";
import { Button } from "@papier/ui/components/ui/button";
import { Card, CardContent } from "@papier/ui/components/ui/card";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { ParentSize } from "@visx/responsive";
import { ArrowLeft } from "@papier/ui/components/icons";
import { TranslateDialog } from "@/components/template/translate-dialog";
import { AiEditDialog } from "@/components/template/ai-edit-dialog";
import {
  TemplateToolsMenu,
  TemplateToolsMenuItems,
} from "@/components/template/template-tools-menu";
import { lazy, Suspense, useRef, useState } from "react";
import type { EmailInitialData } from "@papier/email-editor";

const EmailHandler = lazy(() => import("@/components/edit-handlers/email"));
const MobileHandler = lazy(() => import("@/components/edit-handlers/mobile"));

export const Route = createFileRoute("/_app/projects/$projectSlug/templates/$templateSlug/edit")({
  component: EditTemplate,
  validateSearch: environmentSearch,
  wrapInSuspense: true,
  pendingComponent: () => <EditorLoading className="fixed inset-0 z-50" />,
  errorComponent: ({ error }) => <TemplateErrorComponent error={toError(error)} />,
  loader: async ({ params: { projectSlug, templateSlug }, context: { queryClient, orpc } }) => {
    const projectData = await queryClient.ensureQueryData(
      orpc.projects.getBySlug.queryOptions({ input: { projectSlug } }),
    );
    if (!projectData) {
      notFound();
    }

    const templateData = await queryClient.fetchQuery({
      ...orpc.templates.getDraft.queryOptions({
        input: {
          projectId: projectData.id,
          templateSlug,
        },
      }),
      staleTime: 0,
    });

    return { projectData, templateData };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `Papier - ${loaderData?.templateData.name || "Template"}` }],
  }),
});

function EditTemplate() {
  const { Link, useRouteContext, useLoaderData, useParams } = Route;
  const { env } = Route.useSearch();
  const router = useRouter();
  const { authOptions } = useRouteContext();
  const { projectData } = useLoaderData();
  const { templateSlug } = useParams();
  const queryClient = useQueryClient();
  // Bumped after an AI edit so the editor remounts with the saved content.
  const [reloadKey, setReloadKey] = useState(0);
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const toolsTriggerRef = useRef<HTMLButtonElement>(null);
  // An AI-edited email that the editor still has to render and save.
  const [aiEmail, setAiEmail] = useState<{ localeId: string; content: EmailInitialData } | null>(
    null,
  );

  const { data: user } = useSuspenseQuery(authOptions.session);

  const { data: template } = useSuspenseQuery(
    orpc.templates.getDraft.queryOptions({ input: { templateSlug, projectId: projectData.id } }),
  );

  const availableLocales = template.content?.locales || [];

  const [selectedLocaleCode, setSelectedLocaleCode] = useState<LocaleEnum>(
    availableLocales[0].locale,
  );

  const selectedLocale = availableLocales.find((locale) => locale.locale === selectedLocaleCode)!;

  const draftQueryKey = orpc.templates.getDraft.key({
    input: { templateSlug, projectId: projectData.id },
  });
  const pendingAiEmail = aiEmail?.localeId === selectedLocale.id ? aiEmail.content : null;

  const handleLocaleChange = (newLocaleCode: LocaleEnum) => {
    if (newLocaleCode === selectedLocaleCode) return;
    if (aiEmail) {
      // The AI edit is saved by now; make sure the next locale switch reads fresh data.
      setAiEmail(null);
      void queryClient.invalidateQueries({ queryKey: draftQueryKey });
    }
    setSelectedLocaleCode(newLocaleCode);
  };

  const initialSchemaData = template.schema?.schemaDefinition
    ? JSON.stringify(template.schema.schemaDefinition)
    : JSON.stringify({ schema: [] });

  // SMS/push drafts are free-form JSON ({ html, css, title }); fall back to
  // the placeholder for brand-new drafts that have no saved content yet.
  function toMobileInitialData(content: Record<string, unknown> | null): {
    html: string;
    css: string;
    title?: string;
    image?: string;
    url?: string;
  } {
    const html = content?.["html"];
    const css = content?.["css"];
    const title = content?.["title"];
    const body = content?.["body"];
    const bodyHtml =
      typeof body === "string"
        ? `<p>${body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>`
        : undefined;
    return {
      ...content,
      html: bodyHtml ?? (typeof html === "string" ? html : ""),
      css: typeof css === "string" && css ? css : "{}",
      title: typeof title === "string" && title ? title : undefined,
      image: typeof content?.image === "string" ? content.image : undefined,
      url: typeof content?.url === "string" ? content.url : undefined,
    };
  }

  const userObj = {
    id: user!.user.id,
    name: user!.user.name,
  };

  const projectAction =
    template.status === "published" && template.type === "email" ? (
      <Button
        onClick={() => router.history.back()}
        title="Back"
        aria-label="Back"
        variant="outline"
        size="icon"
      >
        <ArrowLeft className="size-5" />
      </Button>
    ) : (
      <Button
        title="Back to preview"
        aria-label="Back to preview"
        variant="outline"
        size="icon"
        asChild
      >
        <Link to="/projects/$projectSlug/templates/$templateSlug" search={{ env }}>
          <ArrowLeft className="size-5" />
        </Link>
      </Button>
    );
  const languageControl = (
    <LanguageSwitcher
      compact
      label={template.type === "email" ? "Preview language" : "Template language"}
      currentLocale={selectedLocaleCode}
      availableLocales={availableLocales}
      onChange={handleLocaleChange}
    />
  );
  const aiEditDialog =
    template.type !== "document" ? (
      <AiEditDialog
        open={aiEditOpen}
        returnFocusRef={toolsTriggerRef}
        onOpenChange={setAiEditOpen}
        projectId={projectData.id}
        templateSlug={templateSlug}
        locale={selectedLocaleCode}
        onApplied={async (result) => {
          if (result.email) {
            // Emails are rendered and saved by the editor itself, so it loads the edited document.
            setAiEmail({
              localeId: selectedLocale.id,
              content: {
                ...((selectedLocale.content ?? {}) as EmailInitialData),
                document: result.email.document,
                ...(result.email.emailSubject ? { emailSubject: result.email.emailSubject } : {}),
                ...(result.email.emailPreheader
                  ? { emailPreheader: result.email.emailPreheader }
                  : {}),
              },
            });
          } else {
            await queryClient.invalidateQueries({ queryKey: draftQueryKey });
          }
          setReloadKey((key) => key + 1);
        }}
      />
    ) : undefined;
  const translateDialog = (
    <TranslateDialog
      open={translateOpen}
      returnFocusRef={toolsTriggerRef}
      onOpenChange={setTranslateOpen}
      templateContentId={template.content!.id}
      currentLocale={selectedLocaleCode}
      availableLocales={availableLocales}
    />
  );
  const toolDialogs = (
    <>
      {aiEditDialog}
      {translateDialog}
    </>
  );
  const toolMenuItems = (
    <TemplateToolsMenuItems
      onEditWithAi={template.type !== "document" ? () => setAiEditOpen(true) : undefined}
      onTranslate={() => setTranslateOpen(true)}
    />
  );
  const publishActions = (blocked = false) => (
    <>
      <EditorReviewActions
        compact={template.type !== "document"}
        disabled={blocked}
        showStatus={false}
        environmentSlug={env}
        project={projectData}
        templateSlug={templateSlug}
        templateId={template.id}
        contentId={template.contentId}
        availableLocales={availableLocales}
      />
    </>
  );

  return (
    <main className="fixed inset-0 z-50 flex flex-col bg-background">
      {template.type === "document" && (
        <Card className="rounded-none border-0 py-4">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4">
            <div>{projectAction}</div>
            <div className="flex items-center">{languageControl}</div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <TemplateToolsMenu triggerRef={toolsTriggerRef}>{toolMenuItems}</TemplateToolsMenu>
              {toolDialogs}
              {publishActions()}
            </div>
          </CardContent>
        </Card>
      )}
      <div className="min-h-0 flex-1">
        <Suspense fallback={<EditorLoading />}>
          <ParentSize>
            {({ height }) => (
              <>
                {template.type === "email" && (
                  <EmailHandler
                    key={`${selectedLocale.id}-${reloadKey}`}
                    documentId={selectedLocale.id}
                    initialData={
                      pendingAiEmail ?? ((selectedLocale.content ?? {}) as EmailInitialData)
                    }
                    startDirty={pendingAiEmail !== null}
                    projectId={projectData.id}
                    schemaId={template.content!.templateSchemaId}
                    user={userObj}
                    schemaInitialData={initialSchemaData}
                    height={height}
                    templateName={template.name}
                    toolbarStart={projectAction}
                    toolbarCenter={
                      <>
                        {languageControl}
                        {toolDialogs}
                      </>
                    }
                    toolbarMenuItems={toolMenuItems}
                    toolbarMenuTriggerRef={toolsTriggerRef}
                    toolbarEnd={publishActions()}
                  />
                )}
                {template.type === "document" && (
                  <div
                    role="status"
                    className="flex h-full items-center justify-center p-6 text-muted-foreground"
                  >
                    Document editing is unavailable. You can still preview existing documents from
                    the template page.
                  </div>
                )}
                {(template.type === "sms" || template.type === "push_notification") && (
                  <MobileHandler
                    key={`${selectedLocale.id}-${reloadKey}`}
                    projectId={projectData.id}
                    documentId={selectedLocale.id}
                    type={template.type === "sms" ? "sms" : "push"}
                    templateName={template.name}
                    toolbarStart={projectAction}
                    toolbarCenter={
                      <>
                        {languageControl}
                        <TemplateToolsMenu triggerRef={toolsTriggerRef}>
                          {toolMenuItems}
                        </TemplateToolsMenu>
                        {toolDialogs}
                      </>
                    }
                    renderActions={publishActions}
                    initialData={toMobileInitialData(
                      selectedLocale.content as Record<string, unknown> | null,
                    )}
                    initialSchemaData={initialSchemaData}
                    schemaId={template.content!.templateSchemaId}
                    user={userObj}
                  />
                )}
              </>
            )}
          </ParentSize>
        </Suspense>
      </div>
    </main>
  );
}
